import { useState, useEffect } from "react";
import React from "react";
import Icon from "../components/Icon";
import ConfirmDelete from "../components/ConfirmDelete";
import { avatarColor, PHASE_CODES } from "../utils";

// ─── USER MANAGEMENT ──────────────────────────────────────────────────────────
function UserManagement({ t, crmUsers, addCrmUser, updateCrmUser, deleteCrmUser }) {
  const um = t.userMgmt;
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDisplay, setInviteDisplay] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");
  const [inviteStatus, setInviteStatus] = useState(null); // null|"sending"|"sent"|"error"
  const [confirmDel, setConfirmDel] = useState(null);
  const [resetStates, setResetStates] = useState({}); // uid → null|"sending"|"sent"|"error:msg"

  const sendReset = async (u) => {
    setResetStates(p => ({ ...p, [u.id]: "sending" }));
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      const { auth } = await import("../firebase");
      await sendPasswordResetEmail(auth, u.email);
      setResetStates(p => ({ ...p, [u.id]: "sent" }));
      setTimeout(() => setResetStates(p => ({ ...p, [u.id]: null })), 1500);
    } catch (err) {
      setResetStates(p => ({ ...p, [u.id]: `error:${err.message}` }));
      setTimeout(() => setResetStates(p => ({ ...p, [u.id]: null })), 4000);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteStatus("sending");
    try {
      const { db, auth } = await import("../firebase");
      const { collection, addDoc, onSnapshot, serverTimestamp } = await import("firebase/firestore");
      const uid = auth.currentUser?.uid;
      if (!uid) { setInviteStatus("error"); setTimeout(()=>setInviteStatus(null),4000); return; }

      const docRef = await addDoc(collection(db, "pendingUsers"), {
        email: inviteEmail.trim(),
        displayName: inviteDisplay.trim() || inviteEmail.split("@")[0],
        role: inviteRole,
        requestedBy: uid,
        createdAt: serverTimestamp(),
        status: "pending",
      });

      // Poll for result via onSnapshot — unsubscribe on result or after 30s
      let unsub;
      const timeout = setTimeout(() => {
        unsub?.();
        setInviteStatus("error");
        setTimeout(()=>setInviteStatus(null),4000);
      }, 30000);

      unsub = onSnapshot(docRef, (snap) => {
        const status = snap.data()?.status;
        if (!status || status === "pending") return;
        clearTimeout(timeout);
        unsub();
        if (status === "done") {
          setInviteEmail(""); setInviteDisplay(""); setInviteRole("employee");
          setInviteStatus("sent");
          setTimeout(()=>setInviteStatus(null),3000);
        } else if (status === "error") {
          const err = snap.data()?.error;
          setInviteStatus(err === "email-exists" ? "email-exists" : err || "error");
          setTimeout(()=>setInviteStatus(null),4000);
        }
      });
    } catch (err) {
      console.error(err);
      setInviteStatus("error");
      setTimeout(() => setInviteStatus(null), 4000);
    }
  };

  const ROLES = ["admin","foreman","employee"];

  return (
    <div className="settings-section">
      <div className="settings-section-title"><Icon name="shield" size={16}/>{um.title}</div>
      <div className="settings-section-sub">{um.subtitle}</div>

      {(crmUsers||[]).length === 0 ? (
        <div style={{fontSize:13,color:"var(--text-3)",marginBottom:12}}>{um.noUsers}</div>
      ) : (crmUsers||[]).map(u => {
        const rs = resetStates[u.id];
        const resetError = rs?.startsWith("error:") ? rs.slice(6) : null;
        return (
          <div key={u.id}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:resetError?"none":"1px solid var(--border)"}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:avatarColor(u.displayName||u.email),display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:12,fontWeight:700,flexShrink:0}}>
                {(u.displayName||u.email||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.displayName||u.email}</div>
                <div style={{fontSize:11,color:"var(--text-3)"}}>{u.email}</div>
              </div>
              <select className="form-select" style={{width:130,fontSize:12}} value={u.role||"employee"} onChange={e=>updateCrmUser(u.id,{...u,role:e.target.value})}>
                {ROLES.map(r=><option key={r} value={r}>{um[r]}</option>)}
              </select>
              <button
                className="btn btn-secondary btn-sm"
                style={{color:u.active===false?"var(--text-3)":"var(--green)",borderColor:u.active===false?"var(--border)":"var(--green)",minWidth:72}}
                onClick={()=>updateCrmUser(u.id,{...u,active:u.active===false})}
              >
                {u.active===false?um.inactive:um.active}
              </button>
              <button
                className="btn-icon"
                title="Envoyer réinitialisation / Send reset email"
                disabled={rs==="sending"}
                style={{color:rs==="sent"?"var(--green)":rs==="sending"?"var(--text-3)":undefined,opacity:rs?"1":"0.5"}}
                onClick={()=>sendReset(u)}
              >
                {rs==="sending"?"…":<Icon name="mail" size={13}/>}
              </button>
              <button className="btn-icon danger" title={t.common.delete} onClick={()=>setConfirmDel(u)}><Icon name="trash" size={13}/></button>
            </div>
            {resetError&&(
              <div style={{fontSize:11,color:"var(--red)",paddingBottom:8,borderBottom:"1px solid var(--border)"}}>{resetError}</div>
            )}
          </div>
        );
      })}

      <div style={{marginTop:16,padding:"14px",background:"var(--bg)",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)"}}>
        <div style={{fontWeight:600,fontSize:13,marginBottom:10,display:"flex",alignItems:"center",gap:6}}><Icon name="userPlus" size={14}/>{um.invite}</div>
        <div style={{fontSize:12,color:"var(--text-3)",marginBottom:10}}>{um.inviteSub}</div>
        <div className="form-grid form-grid-2" style={{gap:8}}>
          <div className="form-group"><label style={{fontSize:11}}>{um.email} *</label><input className="form-input" type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="employee@example.com"/></div>
          <div className="form-group"><label style={{fontSize:11}}>{um.displayName}</label><input className="form-input" value={inviteDisplay} onChange={e=>setInviteDisplay(e.target.value)} placeholder="Jean Dupont"/></div>
          <div className="form-group">
            <label style={{fontSize:11}}>{um.role}</label>
            <select className="form-select" value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
              {ROLES.map(r=><option key={r} value={r}>{um[r]}</option>)}
            </select>
          </div>
          <div className="form-group" style={{display:"flex",alignItems:"flex-end"}}>
            <button className="btn btn-primary" style={{width:"100%"}} disabled={!inviteEmail||inviteStatus==="sending"} onClick={sendInvite}>
              <Icon name="userPlus" size={13}/>
              {inviteStatus==="sending"?"…"
                :inviteStatus==="sent"?"✓ Utilisateur créé et invitation envoyée / User created and invitation sent"
                :inviteStatus==="email-exists"?"⚠ Un compte avec cet email existe déjà / An account with this email already exists"
                :inviteStatus==="error"?"✗ Erreur — réessayez / Error — try again"
                :inviteStatus?`✗ ${inviteStatus}`
                :um.sendReset}
            </button>
          </div>
        </div>
      </div>

      {confirmDel&&(
        <div className="modal-backdrop" onClick={()=>setConfirmDel(null)}>
          <div className="modal" style={{maxWidth:360}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Retirer utilisateur</div><button className="btn-icon" title={t.common.close} onClick={()=>setConfirmDel(null)}><Icon name="x"/></button></div>
            <div className="modal-body"><p style={{margin:0,fontSize:14}}>Retirer <strong>{confirmDel.displayName||confirmDel.email}</strong> du CRM?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setConfirmDel(null)}>Annuler</button>
              <button className="btn btn-danger" onClick={()=>{deleteCrmUser(confirmDel.id);setConfirmDel(null);}}>Retirer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PHASE CODES SECTION ──────────────────────────────────────────────────────
function PhaseCodesSection({ t, settings, saveSettings }) {
  const merged = PHASE_CODES.map(def => {
    const saved = (settings?.phaseCodes || []).find(p => p.code === def.code);
    return saved ? { ...def, ...saved } : { ...def };
  });
  const [phaseEdits, setPhaseEdits] = useState(merged);
  const [phaseSaved, setPhaseSaved] = useState(false);
  const [phaseCodesOpen, setPhaseCodesOpen] = useState(false);
  const savePhases = async () => {
    await saveSettings({ ...settings, phaseCodes: phaseEdits });
    setPhaseSaved(true);
    setTimeout(() => setPhaseSaved(false), 2000);
  };
  return (
    <div className="settings-section">
      <div className="settings-section-title">
        <button className="btn-icon" title={phaseCodesOpen?t.tooltips.collapseSection:t.tooltips.expandSection} onClick={()=>setPhaseCodesOpen(o=>!o)}><Icon name={phaseCodesOpen?"chevronDown":"chevronRight"} size={13}/></button>
        <Icon name="list" size={16}/>{t.settings.phaseCodesTitle}
      </div>
      {phaseCodesOpen && (
        <>
          <div className="settings-section-sub">{t.settings.phaseCodesSub}</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:12}}>
            <thead>
              <tr>
                <th style={{textAlign:"left",padding:"6px 8px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600,width:"30%"}}>{t.settings.phaseCodesCode}</th>
                <th style={{textAlign:"left",padding:"6px 8px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>{t.settings.phaseCodesLabelFr}</th>
                <th style={{textAlign:"left",padding:"6px 8px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontWeight:600}}>{t.settings.phaseCodesLabelEn}</th>
              </tr>
            </thead>
            <tbody>
              {phaseEdits.map((p, i) => (
                <tr key={p.code}>
                  <td style={{padding:"5px 8px",borderBottom:"1px solid var(--border)",color:"var(--text-3)",fontFamily:"monospace",fontSize:12}}>{p.code}</td>
                  <td style={{padding:"4px 8px",borderBottom:"1px solid var(--border)"}}><input className="form-input" style={{fontSize:12,padding:"4px 8px"}} value={p.labelFr} onChange={e=>{const n=[...phaseEdits];n[i]={...n[i],labelFr:e.target.value};setPhaseEdits(n);}}/></td>
                  <td style={{padding:"4px 8px",borderBottom:"1px solid var(--border)"}}><input className="form-input" style={{fontSize:12,padding:"4px 8px"}} value={p.labelEn} onChange={e=>{const n=[...phaseEdits];n[i]={...n[i],labelEn:e.target.value};setPhaseEdits(n);}}/></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-primary btn-sm" onClick={savePhases}>{phaseSaved ? "✓ Sauvegardé!" : t.settings.phaseCodesSave}</button>
        </>
      )}
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
export const DEFAULT_PERMISSIONS = {
  foreman:  { dashboard:true,  customers:true,  estimates:true, invoices:true, reports:true, jobs:true, schedule:true, timesheets:true, pipeline:false, priceList:false },
  employee: { dashboard:false, customers:false, estimates:false, invoices:false, reports:false, jobs:true, schedule:true, timesheets:true, pipeline:false, priceList:false },
};

function SettingsPage({ t, lang, settings, saveSettings, teamMembers, addTeamMember, updateTeamMember, deleteTeamMember, role, crmUsers, addCrmUser, updateCrmUser, deleteCrmUser, sectionTemplates, deleteSectionTemplate, permissions, savePermissions, crews, updateCrew, materialPhotos, addMaterialPhoto, deleteMaterialPhoto }) {
  const [form, setForm] = useState({...settings, _teamMembers: (teamMembers||[]).map(m=>({...m}))});
  const [saved, setSaved] = useState(false);
  const [tsSaved, setTsSaved] = useState(false);
  const [permForm, setPermForm] = useState(permissions || DEFAULT_PERMISSIONS);
  const [permSaved, setPermSaved] = useState(false);
  const [sectionTemplatesOpen, setSectionTemplatesOpen] = useState(false);
  const [materialLibraryOpen, setMaterialLibraryOpen] = useState(false);
  const [matLibForm, setMatLibForm] = useState({ name:"", caption:"", category:"Material" });
  const [matLibFile, setMatLibFile] = useState(null);
  const [matLibUploading, setMatLibUploading] = useState(false);
  const [matLibConfirm, setMatLibConfirm] = useState(null);
  const [matLibError, setMatLibError] = useState(null);
  const [activeTab, setActiveTab] = useState("company");
  const [crewEdits, setCrewEdits] = useState({});
  const [crewSavedMap, setCrewSavedMap] = useState({});
  useEffect(() => { if (permissions) setPermForm(permissions); }, [permissions]);
  useEffect(() => {
    const edits = {};
    (crews||[]).forEach(c => { edits[c.id] = { name: c.name, color: c.color }; });
    setCrewEdits(edits);
  }, [crews]);

  const save = async () => {
    const { _teamMembers, ...settingsData } = form;
    await saveSettings(settingsData);
    for (const m of (teamMembers||[])) {
      const updated = (_teamMembers||[]).find(x=>x.id===m.id);
      if (!updated) await deleteTeamMember(m.id);
      else if (updated.name!==m.name||updated.role!==m.role||updated.color!==m.color||updated.hourlyRate!==m.hourlyRate||(updated.crewId||null)!==(m.crewId||null)) {
        const rate = updated.hourlyRate!==""&&updated.hourlyRate!==undefined ? Number(updated.hourlyRate)||null : null;
        await updateTeamMember(m.id,{name:updated.name,role:updated.role||"",color:updated.color||"",hourlyRate:rate,crewId:updated.crewId||null});
      }
    }
    for (const m of (_teamMembers||[])) {
      const rate = m.hourlyRate!==""&&m.hourlyRate!==undefined ? Number(m.hourlyRate)||null : null;
      if (!m.id && m.name.trim()) await addTeamMember({name:m.name.trim(),role:m.role||"",color:m.color||"",hourlyRate:rate,crewId:m.crewId||null});
    }
    setSaved(true); setTimeout(()=>setSaved(false),2500);
  };

  const saveTimesheetSettings = async () => {
    const { _teamMembers, ...settingsData } = form;
    await saveSettings({
      ...settings,
      standardStartTime: settingsData.standardStartTime,
      startTimeTolerance: settingsData.startTimeTolerance,
      standardEndTime: settingsData.standardEndTime,
      endTimeTolerance: settingsData.endTimeTolerance,
    });
    setTsSaved(true); setTimeout(()=>setTsSaved(false),2500);
  };

  const savePerms = async () => {
    await savePermissions(permForm);
    setPermSaved(true); setTimeout(()=>setPermSaved(false),2000);
  };

  const F = ({label,k,type="text",full=false}) => (
    <div className="form-group" style={full?{gridColumn:"1/-1"}:{}}>
      <label>{label}</label>
      <input className="form-input" type={type} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}/>
    </div>
  );
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(p=>({...p,logoBase64:ev.target.result}));
    reader.readAsDataURL(file);
  };
  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(p=>({...p,signatureBase64:ev.target.result}));
    reader.readAsDataURL(file);
  };

  const TABS = [
    { id:"company",    label: lang==="fr" ? "Entreprise"  : "Company"    },
    { id:"team",       label: lang==="fr" ? "Équipe"      : "Team"       },
    { id:"operations", label: lang==="fr" ? "Opérations"  : "Operations" },
    { id:"timesheets", label: lang==="fr" ? "Pointage"    : "Timesheets" },
  ];

  const PERM_MODULES = [
    {key:"dashboard", label:t.nav.dashboard},
    {key:"customers", label:t.nav.customers},
    {key:"pipeline",  label:t.nav.leads},
    {key:"estimates", label:t.nav.estimates},
    {key:"invoices",  label:t.nav.invoices},
    {key:"jobs",      label:t.nav.jobs},
    {key:"schedule",  label:t.nav.schedule},
    {key:"priceList", label:t.nav.priceList},
    {key:"reports",   label:t.nav.reports},
    {key:"timesheets",label:t.nav.timesheets},
    {key:"truckLog",  label:"Voyages / Loads"},
  ];
  const togglePerm = (roleKey, moduleKey) => setPermForm(p => ({
    ...p,
    [roleKey]: { ...p[roleKey], [moduleKey]: !p[roleKey][moduleKey] },
  }));

  return (
    <div>
      <div className="page-header"><div className="page-header-row"><div><h1>{t.settings.title}</h1><p>{t.settings.subtitle}</p></div></div></div>
      <div className="page-body">

        {/* ── Tab Bar ── */}
        <div className="settings-tabs">
          {TABS.map(tab=>(
            <button key={tab.id} className={`settings-tab-btn${activeTab===tab.id?" active":""}`} onClick={()=>setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab 1: Entreprise ── */}
        {activeTab==="company"&&(
          <>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="building" size={16}/>{t.settings.companyProfile} <span style={{fontSize:11,fontWeight:400,color:"var(--text-3)",marginLeft:4}}>{t.settings.companyProfileSub}</span></div>
              <div className="form-grid form-grid-2">
                <F label={`${t.settings.companyName} *`} k="companyName" full/>
                <F label={t.settings.phone} k="phone"/>
                <F label={t.settings.email} k="email" type="email"/>
                <F label={t.settings.website} k="website"/>
                <div/>
                <F label={t.settings.address} k="address" full/>
                <F label={t.settings.city} k="city"/>
                <F label={t.settings.province} k="province"/>
                <F label={t.settings.postal} k="postal"/>
                <div/>
                <F label={t.settings.gst} k="gst"/>
                <F label={t.settings.qst} k="qst"/>
              </div>
            </div>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="upload" size={16}/>{t.settings.logo}</div>
              <div className="settings-section-sub">Affiché sur toutes les soumissions, factures et rapports de production</div>
              <div style={{display:"flex",alignItems:"center",gap:16,padding:"16px",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",background:"var(--bg)"}}>
                <div style={{width:80,height:80,background:"#f3f4f6",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0,border:"1px solid var(--border)"}}>
                  {form.logoBase64
                    ? <img src={form.logoBase64} style={{width:"100%",height:"100%",objectFit:"contain"}} alt="logo"/>
                    : <div style={{width:60,height:60,background:"#b91c1c",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:12,textAlign:"center",padding:4}}>{form.companyName?.split(" ").slice(0,3).map(w=>w[0]).join("")||"LOGO"}</div>
                  }
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <label className="btn btn-secondary" style={{cursor:"pointer",margin:0}}>
                    <Icon name="upload" size={14}/>{t.settings.replace}
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={handleLogoUpload}/>
                  </label>
                  {form.logoBase64&&<button className="btn btn-danger btn-sm" onClick={()=>setForm(p=>({...p,logoBase64:null}))}><Icon name="x" size={12}/>{t.settings.remove}</button>}
                  <div style={{fontSize:11,color:"var(--text-3)"}}>PNG, JPG · recommandé 200×200px</div>
                </div>
              </div>
            </div>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="edit" size={16}/>Signature autorisée</div>
              <div className="settings-section-sub">Signature du représentant — apparaît sur les soumissions, factures et rapports</div>
              <div className="form-grid form-grid-2" style={{marginBottom:14}}>
                <div className="form-group" style={{gridColumn:"1/-1"}}>
                  <label>Nom du représentant autorisé</label>
                  <input className="form-input" value={form.authorizedName||""} onChange={e=>setForm(p=>({...p,authorizedName:e.target.value}))} placeholder="Ex: Mike Amyotte"/>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:16,padding:"16px",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",background:"var(--bg)"}}>
                <div style={{minWidth:160,height:72,background:"#f3f4f6",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0,border:"1px solid var(--border)",padding:8}}>
                  {form.signatureBase64
                    ? <img src={form.signatureBase64} style={{maxHeight:"100%",maxWidth:"100%",objectFit:"contain"}} alt="signature"/>
                    : <div style={{fontSize:11,color:"var(--text-3)",textAlign:"center"}}>Aucune signature</div>
                  }
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <label className="btn btn-secondary" style={{cursor:"pointer",margin:0}}>
                    <Icon name="upload" size={14}/>Télécharger signature
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={handleSignatureUpload}/>
                  </label>
                  {form.signatureBase64&&<button className="btn btn-danger btn-sm" onClick={()=>setForm(p=>({...p,signatureBase64:null}))}><Icon name="x" size={12}/>Supprimer</button>}
                  <div style={{fontSize:11,color:"var(--text-3)"}}>PNG avec fond transparent recommandé</div>
                </div>
              </div>
            </div>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="estimates" size={16}/>{t.settings.terms}</div>
              <div className="settings-section-sub">Page séparée à la fin des soumissions et factures (pas sur les rapports de production)</div>
              <div className="form-group">
                <textarea className="form-textarea" style={{minHeight:200,fontFamily:"var(--font-body)",fontSize:13,lineHeight:1.7}} value={form.terms||""} onChange={e=>setForm(p=>({...p,terms:e.target.value}))} placeholder="Entrez vos modalités et conditions ici..."/>
              </div>
            </div>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="mail" size={16}/>{lang==="fr"?"Courriel de suivi":"Follow-up Email"}</div>
              <div className="settings-section-sub">{lang==="fr"?"Envoyé automatiquement après qu'une facture est payée en totalité.":"Sent automatically after an invoice is paid in full."}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div className="form-group">
                  <label>Français</label>
                  <textarea className="form-textarea" style={{minHeight:120,fontFamily:"var(--font-body)",fontSize:13,lineHeight:1.6}} value={form.thankYouEmailFR||""} onChange={e=>setForm(p=>({...p,thankYouEmailFR:e.target.value}))} placeholder={"Bonjour {clientName},\n\nMerci pour votre confiance..."}/>
                </div>
                <div className="form-group">
                  <label>English</label>
                  <textarea className="form-textarea" style={{minHeight:120,fontFamily:"var(--font-body)",fontSize:13,lineHeight:1.6}} value={form.thankYouEmailEN||""} onChange={e=>setForm(p=>({...p,thankYouEmailEN:e.target.value}))} placeholder={"Hello {clientName},\n\nThank you for choosing..."}/>
                </div>
              </div>
              <div style={{fontSize:11,color:"var(--text-3)",marginTop:4}}>{lang==="fr"?"{clientName} sera remplacé par le prénom du client":"{clientName} will be replaced with the client's first name"}</div>
            </div>
            <div style={{marginBottom:24}}>
              <button className="btn btn-primary" onClick={save}><Icon name="building" size={14}/>{saved?"✓ Sauvegardé!":t.settings.save}</button>
            </div>
          </>
        )}

        {/* ── Tab 2: Équipe ── */}
        {activeTab==="team"&&(
          <>
            {/* Section 1 — Employés */}
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="users" size={16}/>Employés / Employees</div>
              <div className="settings-section-sub">Nom, rôle, équipe assignée et taux horaire (admin seulement)</div>
              {(form._teamMembers||[]).map((m,i)=>(
                <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:m.color||'#2563eb',flexShrink:0}}/>
                  <input className="form-input" style={{flex:"1 1 120px",minWidth:80}} placeholder="Nom" value={m.name||""} onChange={e=>{const tm=[...(form._teamMembers||[])];tm[i]={...tm[i],name:e.target.value};setForm(p=>({...p,_teamMembers:tm}));}}/>
                  <input className="form-input" style={{flex:"1 1 100px",minWidth:80}} placeholder="Rôle (ex: Foreman)" value={m.role||""} onChange={e=>{const tm=[...(form._teamMembers||[])];tm[i]={...tm[i],role:e.target.value};setForm(p=>({...p,_teamMembers:tm}));}}/>
                  {/* Crew dropdown */}
                  <select className="form-select" style={{flex:"1 1 120px",minWidth:100,fontSize:12}} value={m.crewId||""} onChange={e=>{const tm=[...(form._teamMembers||[])];tm[i]={...tm[i],crewId:e.target.value||null};setForm(p=>({...p,_teamMembers:tm}));}}>
                    <option value="">— Aucune équipe —</option>
                    {(crews||[]).map(c=>(
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {role==="admin"&&(
                    <div style={{display:"flex",alignItems:"center",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",overflow:"hidden",background:"var(--surface)",flexShrink:0}}>
                      <span style={{padding:"0 7px",fontSize:13,color:"var(--text-3)",borderRight:"1px solid var(--border)",lineHeight:"32px",userSelect:"none"}}>$</span>
                      <input
                        type="number"
                        className="form-input"
                        style={{width:72,border:"none",borderRadius:0,padding:"0 8px",fontSize:13}}
                        placeholder="0.00"
                        min={0}
                        step={0.01}
                        value={m.hourlyRate??""}
                        onFocus={e=>e.target.select()}
                        onChange={e=>{const tm=[...(form._teamMembers||[])];tm[i]={...tm[i],hourlyRate:e.target.value};setForm(p=>({...p,_teamMembers:tm}));}}
                        title="Taux horaire / Hourly Rate"
                      />
                    </div>
                  )}
                  <input type="color" value={m.color||'#2563eb'} title="Couleur personnelle" style={{width:32,height:32,padding:2,border:"1px solid var(--border)",borderRadius:4,cursor:"pointer",background:"none"}} onChange={e=>{const tm=[...(form._teamMembers||[])];tm[i]={...tm[i],color:e.target.value};setForm(p=>({...p,_teamMembers:tm}));if(m.id)updateTeamMember(m.id,{...m,color:e.target.value});}}/>
                  <button className="btn-icon danger" title={t.common.delete} onClick={()=>setForm(p=>({...p,_teamMembers:(p._teamMembers||[]).filter((_,j)=>j!==i)}))}>
                    <Icon name="x" size={13}/>
                  </button>
                </div>
              ))}
              {role==="admin"&&(
                <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>$ Taux horaire — visible aux admins seulement</div>
              )}
              <button className="btn btn-secondary btn-sm" style={{marginTop:4}} onClick={()=>setForm(p=>({...p,_teamMembers:[...(p._teamMembers||[]),{name:"",role:"",hourlyRate:"",crewId:""}]}))}>
                <Icon name="plus" size={12}/>Ajouter employé
              </button>
            </div>
            <div style={{marginBottom:24}}>
              <button className="btn btn-primary" onClick={save}><Icon name="users" size={14}/>{saved?"✓ Sauvegardé!":t.settings.save}</button>
            </div>

            {/* Section 2 — Équipes / Crews */}
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="schedule" size={16}/>Équipes / Crews</div>
              <div className="settings-section-sub">Nom et couleur de chaque équipe. Les membres sont assignés via la liste des employés ci-dessus.</div>
              {(crews||[]).map(crew=>{
                const edit = crewEdits[crew.id] || { name: crew.name, color: crew.color };
                const crewMembers = (teamMembers||[]).filter(m=>m.crewId===crew.id);
                return (
                  <div key={crew.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
                    <input
                      type="color"
                      value={edit.color||crew.color}
                      title="Couleur de l'équipe"
                      style={{width:36,height:36,padding:2,border:"1px solid var(--border)",borderRadius:4,cursor:"pointer",background:"none",flexShrink:0}}
                      onChange={e=>setCrewEdits(p=>({...p,[crew.id]:{...edit,color:e.target.value}}))}
                    />
                    <div style={{flex:1}}>
                      <input
                        className="form-input"
                        style={{marginBottom:6,fontWeight:600}}
                        value={edit.name}
                        onChange={e=>setCrewEdits(p=>({...p,[crew.id]:{...edit,name:e.target.value}}))}
                        placeholder="Nom de l'équipe"
                      />
                      {crewMembers.length>0?(
                        <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                          {crewMembers.map(m=>(
                            <span key={m.id} style={{fontSize:11,padding:"2px 8px",background:(edit.color||crew.color)+"20",color:(edit.color||crew.color),border:`1px solid ${edit.color||crew.color}40`,borderRadius:12,fontWeight:500}}>
                              {m.name}
                            </span>
                          ))}
                        </div>
                      ):(
                        <div style={{fontSize:11,color:"var(--text-3)"}}>Aucun membre assigné</div>
                      )}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{flexShrink:0}}
                      onClick={async()=>{
                        await updateCrew(crew.id,{...crew,name:edit.name,color:edit.color});
                        setCrewSavedMap(p=>({...p,[crew.id]:true}));
                        setTimeout(()=>setCrewSavedMap(p=>({...p,[crew.id]:false})),2000);
                      }}
                    >
                      {crewSavedMap[crew.id]?"✓":lang==="fr"?"Sauvegarder":"Save"}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Section 3 — Comptes utilisateurs */}
            {role==="admin"&&<UserManagement t={t} crmUsers={crmUsers} addCrmUser={addCrmUser} updateCrmUser={updateCrmUser} deleteCrmUser={deleteCrmUser}/>}
            {role==="admin"&&(
              <div className="settings-section">
                <div className="settings-section-title"><Icon name="shield" size={16}/>{t.settings.permissionsTitle}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
                  {["foreman","employee"].map(roleKey => (
                    <div key={roleKey}>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:8,color:"var(--text-2)"}}>
                        {roleKey==="foreman" ? t.settings.permissionsForeman : t.settings.permissionsEmployee}
                      </div>
                      {PERM_MODULES.map(({key,label})=>(
                        <div key={key} className="report-toggle-row" onClick={()=>togglePerm(roleKey,key)}>
                          <input type="checkbox" checked={!!permForm[roleKey]?.[key]} onChange={()=>{}}/>
                          <label>{label}</label>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary" style={{marginTop:16}} onClick={savePerms}><Icon name="shield" size={14}/>{permSaved ? t.settings.permissionsSaved : t.settings.permissionsSave}</button>
              </div>
            )}
          </>
        )}

        {/* ── Tab 3: Opérations ── */}
        {activeTab==="operations"&&(
          <>
            <div className="settings-section">
              <div className="settings-section-title">
                <button className="btn-icon" title={sectionTemplatesOpen?t.tooltips.collapseSection:t.tooltips.expandSection} onClick={()=>setSectionTemplatesOpen(o=>!o)}><Icon name={sectionTemplatesOpen?"chevronDown":"chevronRight"} size={13}/></button>
                <Icon name="bookmark" size={16}/>{t.settings.sectionTemplatesTitle}
              </div>
              {sectionTemplatesOpen && (
                <>
                  <div className="settings-section-sub">{t.settings.sectionTemplatesSub}</div>
                  {(sectionTemplates||[]).length===0
                    ? <div style={{fontSize:13,color:"var(--text-3)",padding:"12px 0"}}>{t.estimates.noSectionTemplates}</div>
                    : (sectionTemplates||[]).map(tmpl=>(
                        <div key={tmpl.id} className="list-row" style={{padding:"10px 0"}}>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:600,fontSize:14}}>{tmpl.title||tmpl.name}</div>
                            <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>
                              {tmpl.category&&<span className="badge badge-gray" style={{marginRight:6}}>{tmpl.category}</span>}
                              {(tmpl.items||[]).length} items
                              {tmpl.description&&<span> — {tmpl.description}</span>}
                            </div>
                          </div>
                          <button className="btn-icon danger" title={t.tooltips.deleteTemplate} onClick={()=>deleteSectionTemplate(tmpl.id)}><Icon name="trash" size={13}/></button>
                        </div>
                      ))
                  }
                </>
              )}
            </div>
            {role==="admin"&&(
              <div className="settings-section">
                <div className="settings-section-title">
                  <button className="btn-icon" title={materialLibraryOpen?t.tooltips.collapseSection:t.tooltips.expandSection} onClick={()=>setMaterialLibraryOpen(o=>!o)}><Icon name={materialLibraryOpen?"chevronDown":"chevronRight"} size={13}/></button>
                  <Icon name="upload" size={16}/>{t.settings.materialLibrary}
                </div>
                {materialLibraryOpen&&(
                  <>
                    <div className="settings-section-sub">{t.settings.materialLibrarySub}</div>
                    {/* Existing photos */}
                    {(materialPhotos||[]).length>0&&(
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:12,marginBottom:16}}>
                        {(materialPhotos||[]).map(photo=>(
                          <div key={photo.id} style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",position:"relative"}}>
                            <img src={photo.photoURL} alt={photo.name} style={{width:"100%",aspectRatio:"4/3",objectFit:"cover",display:"block"}}/>
                            <div style={{padding:"8px 10px"}}>
                              <div style={{fontWeight:600,fontSize:13,marginBottom:2}}>{photo.name}</div>
                              {photo.caption&&<div style={{fontSize:11,color:"var(--text-3)",marginBottom:3}}>{photo.caption}</div>}
                              <span className="badge badge-gray" style={{fontSize:10}}>{photo.category}</span>
                            </div>
                            <button className="btn-icon danger" title={t.common.delete} style={{position:"absolute",top:6,right:6,background:"rgba(0,0,0,.45)",borderRadius:4}} onClick={()=>setMatLibConfirm(photo)}><Icon name="trash" size={12}/></button>
                          </div>
                        ))}
                      </div>
                    )}
                    {(materialPhotos||[]).length===0&&(
                      <div style={{fontSize:13,color:"var(--text-3)",padding:"10px 0 12px"}}>{t.estimates.noMaterialPhotos}</div>
                    )}
                    {/* Add form */}
                    <div style={{border:"1px solid var(--border)",borderRadius:8,padding:14,background:"var(--bg-2)"}}>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>{t.settings.addMaterial}</div>
                      <div className="form-grid form-grid-2" style={{gap:10}}>
                        <div className="form-group" style={{gridColumn:"1/-1"}}>
                          <label style={{fontSize:12}}>{lang==="fr"?"Photo":"Photo"}</label>
                          <input type="file" accept="image/*" className="form-input" style={{fontSize:12,padding:"5px 8px"}} onChange={e=>{setMatLibFile(e.target.files?.[0]||null);setMatLibError(null);}}/>
                        </div>
                        <div className="form-group">
                          <label style={{fontSize:12}}>{t.settings.materialName} *</label>
                          <input className="form-input" style={{fontSize:12}} value={matLibForm.name} onChange={e=>setMatLibForm(p=>({...p,name:e.target.value}))} placeholder={lang==="fr"?"ex. Pierre nette 3/4\"":"e.g. 3/4\" Clean Stone"}/>
                        </div>
                        <div className="form-group">
                          <label style={{fontSize:12}}>{t.settings.materialCategory}</label>
                          <select className="form-select" style={{fontSize:12}} value={matLibForm.category} onChange={e=>setMatLibForm(p=>({...p,category:e.target.value}))}>
                            {["Material","Equipment","Other"].map(c=><option key={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="form-group" style={{gridColumn:"1/-1"}}>
                          <label style={{fontSize:12}}>{t.settings.materialCaption}</label>
                          <input className="form-input" style={{fontSize:12}} value={matLibForm.caption} onChange={e=>setMatLibForm(p=>({...p,caption:e.target.value}))} placeholder={lang==="fr"?"Description courte...":"Short description..."}/>
                        </div>
                      </div>
                      {matLibError&&<div style={{color:"var(--red)",fontSize:12,marginBottom:8}}>{matLibError}</div>}
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={matLibUploading||!matLibFile||!matLibForm.name.trim()}
                        onClick={async()=>{
                          if(!matLibFile||!matLibForm.name.trim()) return;
                          setMatLibUploading(true);
                          setMatLibError(null);
                          try {
                            await addMaterialPhoto(matLibFile, matLibForm.name.trim(), matLibForm.caption.trim(), matLibForm.category);
                            setMatLibForm({name:"",caption:"",category:"Material"});
                            setMatLibFile(null);
                            const input = document.querySelector('input[type="file"][accept="image/*"]');
                            if(input) input.value="";
                          } catch(err) {
                            setMatLibError(err.message||"Erreur");
                          } finally {
                            setMatLibUploading(false);
                          }
                        }}
                      >
                        <Icon name="upload" size={13}/>{matLibUploading?(lang==="fr"?"Chargement...":"Uploading..."):t.settings.addMaterial}
                      </button>
                    </div>
                    {/* Delete confirmation */}
                    {matLibConfirm&&(
                      <ConfirmDelete
                        label={matLibConfirm.name}
                        onConfirm={async()=>{await deleteMaterialPhoto(matLibConfirm);setMatLibConfirm(null);}}
                        onCancel={()=>setMatLibConfirm(null)}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {role==="admin"&&<PhaseCodesSection t={t} settings={settings} saveSettings={saveSettings}/>}
          </>
        )}

        {/* ── Tab 4: Pointage ── */}
        {activeTab==="timesheets"&&(
          <>
            <div className="settings-section">
              <div className="settings-section-title"><Icon name="clock" size={16}/>{t.timesheets.pointageSettings}</div>
              <div className="form-grid form-grid-2" style={{gap:12}}>
                <div className="form-group"><label style={{fontSize:12}}>{t.timesheets.standardStartTime}</label><input type="time" className="form-input" value={form.standardStartTime||"07:00"} onChange={e=>setForm(p=>({...p,standardStartTime:e.target.value}))}/></div>
                <div className="form-group"><label style={{fontSize:12}}>{t.timesheets.startTimeTolerance}</label><input type="number" className="form-input" min={0} max={60} value={form.startTimeTolerance??15} onChange={e=>setForm(p=>({...p,startTimeTolerance:parseInt(e.target.value)||0}))}/></div>
                <div className="form-group"><label style={{fontSize:12}}>{t.timesheets.standardEndTime}</label><input type="time" className="form-input" value={form.standardEndTime||""} onChange={e=>setForm(p=>({...p,standardEndTime:e.target.value}))}/></div>
                <div className="form-group"><label style={{fontSize:12}}>{t.timesheets.endTimeTolerance}</label><input type="number" className="form-input" min={0} max={60} value={form.endTimeTolerance??15} onChange={e=>setForm(p=>({...p,endTimeTolerance:parseInt(e.target.value)||0}))}/></div>
              </div>
            </div>
            <div style={{marginBottom:24}}>
              <button className="btn btn-primary" onClick={saveTimesheetSettings}><Icon name="clock" size={14}/>{tsSaved?"✓ Sauvegardé!":t.settings.save}</button>
            </div>
          </>
        )}

        {/* ── Danger Zone (always visible) ── */}
        <div className="danger-section">
          <div className="danger-section-title">{t.settings.dangerZone}</div>
          <div className="danger-row"><div><div style={{fontWeight:500}}>{t.settings.deleteAccount}</div><div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{t.settings.deleteAccountSub}</div></div><button className="btn btn-danger"><Icon name="trash" size={14}/>{t.settings.delete}</button></div>
        </div>

      </div>
    </div>
  );
}

export default SettingsPage;
